import { useQuery } from "@tanstack/react-query";
import { useState, useCallback, useMemo, useEffect } from "react";
import axios from "../../../services/axiosInstance";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useGBP } from "./useGBP";
import { usePagination } from "./usePagination";
import type { GBP } from "../types/Listing";

export interface AccountCard {
  id: string;
  name: string;
  resourceName: string;
}

export interface LocationApi {
  name?: string;
  title?: string;
  websiteUri?: string;
  metadata?: { mapsUri?: string };
  mapsUri?: string;
  phoneNumbers?: {
    primaryPhone?: string;
    additionalPhones?: string[];
  };
  openInfo?: {
    status?: string;
    openingDate?: { year?: number; month?: number; day?: number };
  };
  storefrontAddress?: {
    regionCode?: string;
    postalCode?: string;
    locality?: string;
    addressLines?: string[];
    administrativeArea?: string;
  };
  categories?: {
    primaryCategory?: { name?: string; displayName?: string };
    additionalCategories?: { name?: string; displayName?: string }[];
  };
  callClicks?: number | null;
  status?: {
    verified: boolean;
    published: boolean;
    suspended: boolean;
    needsReverification: boolean;
    open?: string | null;
    enriched?: boolean;
  };
}

interface GBPAccountsResult {
  accounts: AccountCard[];
  locations: LocationApi[];
}

const buildParams = (startDate?: string, endDate?: string, subjectEmail?: string) => {
  const params = new URLSearchParams();
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (subjectEmail) params.append("subjectEmail", subjectEmail);
  return params.toString();
};

 export const useGBPAccounts = (
  accountId: string,
  enabled: boolean,
  startDate?: string,
  endDate?: string,
  subjectEmail?: string
) =>
  useQuery<GBPAccountsResult>({
    queryKey: ["gbp", "accounts", accountId, startDate, endDate, subjectEmail],
    queryFn: async () => {
      const res = await axios.get(`/api/gbp/accounts?${buildParams(undefined, undefined, subjectEmail)}`);
      const accounts: AccountCard[] = res.data?.accounts || [];

      if (!accountId) return { accounts, locations: [] };

      try {
        const locRes = await axios.get(
          `/api/gbp/accounts/${encodeURIComponent(accountId)}/locations?${buildParams(startDate, endDate, subjectEmail)}`
        );
        const locations: LocationApi[] = locRes.data?.locations || [];
        return { accounts, locations };
      } catch {
        return { accounts, locations: [] };
      }
    },
    enabled,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const useAccountLocations = (
  accountId: string | null,
  enabled: boolean,
  startDate?: string,
  endDate?: string,
  subjectEmail?: string
) =>
  useQuery<LocationApi[]>({
    queryKey: ["gbp", "locations", accountId, startDate, endDate, subjectEmail],
    queryFn: async () => {
      if (!accountId) return [];
      const res = await axios.get(
        `/api/gbp/accounts/${encodeURIComponent(accountId)}/locations?${buildParams(startDate, endDate, subjectEmail)}`
      );
      return res.data?.locations || [];
    },
    enabled: enabled && !!accountId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

 export const useFilteredLocations = (
  locations: LocationApi[],
  search: string,
  page: number,
  perPage: number
) => {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? locations.filter((l) => (l.title || "").toLowerCase().includes(q))
      : locations;
  }, [locations, search]);

  const { total, pageItems } = usePagination(filtered, page, perPage);
  return { total, pageItems };
};

 export const useGBPListingManagement = (websiteId?: number, subjectEmail?: string,  gbpAccountId?: string) => {
  const { user } = useAuth();
   const gbp = useGBP(websiteId);

  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const ago29 = new Date();
    ago29.setDate(today.getDate() - 29);
    return { startDate: ago29.toISOString().slice(0, 10), endDate: today.toISOString().slice(0, 10) };
  });

  const [selectedAccount, setSelectedAccount] = useState<AccountCard | null>(null);

  const [selectedGbpAccountId, setSelectedGbpAccountId] = useState<string>(
    () => (typeof window !== "undefined" ? sessionStorage.getItem("gbp_selected_account_id") || "" : "")
  );

  useEffect(() => {
    const id = sessionStorage.getItem("gbp_selected_account_id") || "";
    setSelectedGbpAccountId(id);
  }, [websiteId]);

  const datesReady = !!dateRange.startDate && !!dateRange.endDate;

  const accountsQuery = useGBPAccounts(selectedGbpAccountId, datesReady, dateRange.startDate, dateRange.endDate, subjectEmail);

  const gbpAccounts = accountsQuery.data?.accounts || [];
  const allLocations = useMemo(() => accountsQuery.data?.locations || [], [accountsQuery.data?.locations]);

  const locationsQuery = useAccountLocations(selectedAccount?.id || null, !!selectedAccount, dateRange.startDate, dateRange.endDate, subjectEmail);
  const gLocations = locationsQuery.data || [];
const gOnlyLocations = useMemo(() => {
  return allLocations;
}, [allLocations]);


  /*const gOnlyLocations = useMemo(() => {
    if (!allLocations.length) return [];
    if (user?.isAdmin) return allLocations;
    return allLocations.filter((loc) => {
    });
  }, [allLocations, user, gbp.allGbpFiches]);
*/
  const normalizeHost = useCallback((url?: string | null) => {
    if (!url) return "";
    try {
      return new URL(url).hostname.toLowerCase();
    } catch {
      return url.trim().replace(/\/$/, "").toLowerCase();
    }
  }, []);



  const handleOpenPerformance = useCallback(
    (g: GBP, setPerfLocationName: (s?: string) => void, setPerfFicheName: (s?: string) => void) => {
      if (!g.locationId) {
        toast.error("Aucun identifiant de fiche disponible.");
        return;
      }
      setPerfLocationName(g.locationId);
      setPerfFicheName(g.name || g.locationId);
    },
    []
  );

  const handleOpenReviews = useCallback(
    (g: GBP, setReviewsModalOpen: (b: boolean) => void, setReviewsLocationName: (n?: string) => void, setReviewsFicheId: (id?: number) => void) => {
      setReviewsLocationName(g.locationId);
      setReviewsFicheId(undefined);
      setReviewsModalOpen(true);
    },
    []
  );

  return {
    user,
    gbp,
    gbpAccounts,
    gLocations,
    gOnlyLocations,
    accountsLoading: accountsQuery.isLoading,
    accountsError: accountsQuery.isError ? (accountsQuery.error as any)?.response?.data?.message : null,
    selectedAccount,
    setSelectedAccount,
    normalizeHost,
    handleOpenPerformance,
    handleOpenReviews,
    selectedGbpAccountId,
    dateRange,
    setDateRange,
  };
};